"use client";

import { useEffect, useRef } from "react";
import { apiBase } from "./api";
import { watchPreviewStream } from "./preview/watch-preview-stream";
import {
  RECONNECT_BASE_MS,
  RECONNECT_MAX_MS,
  STABLE_OPEN_MS,
} from "./use-visitor-threads-realtime";
import { useWidgetInstance } from "./widget-instance-context";

/** Sender attribution. BE shape uses `from` (visitor / operator / bot
 *  / ai / system). The widget previously read `sender` — keep both
 *  for back-compat so a deploy mismatch doesn't blank the avatar
 *  line. */
export interface RealtimeSender {
  kind: "operator" | "ai" | "bot" | "system" | "visitor";
  name: string;
  avatar?: string;
}

/** Quick-reply chip shape on the wire. BE emits `{ label, value, type }`;
 *  the widget's button renderer expects `{ text, payload, style }`. The
 *  SSE consumer normalises one to the other.
 *
 *  `type === "callback"` is the only widely-used variant — the value
 *  identifies a flow branch the bot routes on. Other types map through
 *  unchanged. */
export interface RealtimeMessageButton {
  label: string;
  value: string;
  type?: string;
  /** Set for `url` buttons — the external link to open. */
  url?: string;
  /** Set for `phone` buttons — the number for a `tel:` link. */
  phoneNumber?: string;
}

export interface RealtimeMessageEvent {
  /** Backend message id once persisted; client-temp id while in-flight.
   *  For `kind === 'read-receipt'` this is a synthetic `read-<ts>` id
   *  the BE mints and the visitor ignores. */
  externalMessageId: string;
  /** Optional — the new BE shape omits `direction` and lets the
   *  consumer derive it from `from.kind` (visitor → OUTBOUND, else
   *  INBOUND). Kept for back-compat with older event emitters. */
  direction?: "INBOUND" | "OUTBOUND" | string;
  kind:
    | "text"
    | "media"
    | "buttons"
    | "location"
    | "typing"
    | "read-receipt"
    | "assignment"
    | "status"
    | "blocked"
    | string;
  text?: string;
  /** Set client-side when the event arrived as the SSE `messageUpdate` event: an EDIT of the
   *  message with this `externalMessageId`, not a new message. */
  update?: boolean;
  /** Set client-side when the event arrived as the SSE `messageDelete` event: the message with this
   *  `externalMessageId` was deleted. Carries no body. */
  deleted?: boolean;
  /** Set when `kind === 'status'` — the conversation's `ChatStatus` changed
   *  (UPPERCASE: 'CLOSED' / 'RESOLVED' / 'IN_PROGRESS' / …). Carries no message
   *  body; drives the composer lock, never a bubble. */
  status?: string;
  /** Set when `kind === 'blocked'` — a block state change forwarded on the
   *  per-chat stream. `blockScope` says the level ('CONTACT' = whole visitor,
   *  'THREAD' = this thread). Never a bubble; the visitor-level threads stream
   *  is the primary channel that drives the composer lock. */
  blocked?: boolean;
  blockScope?: "THREAD" | "CONTACT" | string;
  payload?: unknown;
  /** Quick-reply chips for `kind === 'buttons'` messages. */
  buttons?: RealtimeMessageButton[];
  /** AI-reply citations (the help/news/KB pages the answer drew from).
   *  Attached by presence on AI messages, like `buttons`/`media`; rendered as
   *  the "Sources" block under the bubble, never inline in the reply text. */
  sources?: Array<{
    link?: string | null;
    seoTitle?: string | null;
    seoDescription?: string | null;
  }>;
  /** New shape — preferred. */
  from?: RealtimeSender;
  /** Legacy shape — older BE deploys emit this name; keep as a
   *  fallback so a deploy mismatch doesn't lose attribution. */
  sender?: RealtimeSender;
  sentAt?: string;
  /** Set on AI typing events — which step of the assistant turn is
   *  running: `"thinking"` (prep / STT / deciding), `"searching"` (RAG
   *  knowledge lookup), `"typing"` (model producing the answer). Drives
   *  the typing-bubble caption. Absent for operator typing. */
  phase?: "thinking" | "searching" | "typing" | string;
  /** Set when `kind === 'read-receipt'`. Carries the exact message
   *  ids that flipped to READ — the iframe matches against its
   *  local store and upgrades just those bubbles. */
  readReceipt?: {
    messageIds: string[];
    readAt: string;
  };
  /** Set when `kind === 'assignment'` — an operator-handoff status change.
   *  `messageId` is the id of the persisted OPERATOR_CONNECTED / CHAT_RESOLVED
   *  history item (present on connected/resolved, `null` on pending — no
   *  persistent message is created for pending). `operator` is present only on
   *  connected. */
  assignment?: {
    assignedStatus: "pending" | "connected" | null;
    messageId: string | null;
    operator?: { name: string; avatar?: string };
    /** Set on the resolve event (`assignedStatus: null`) — who ended the
     *  handoff. Drives the "resolved by you / by {operator}" copy live. */
    initiator?: "VISITOR" | "OPERATOR" | "SYSTEM";
    /** Set only on `pending` — estimated time to the operator's first reply
     *  (flat `VisitorAssignmentEstimate` shape, same as the query). `basis`
     *  picks the field: `operator_load` → `etaMinutes` or `soft`;
     *  `business_hours` → `availableAt` (ISO next-open time). */
    estimate?: {
      basis: string;
      etaMinutes?: number | null;
      availableAt?: string | null;
      soft?: boolean | null;
    };
  };
}

interface UseVisitorRealtimeOpts {
  chatId: string | null;
  token: string | null;
  /** Who the token belongs to; a DIFFERENT visitor reconnects, a renewal of the same one does not. */
  visitorId?: string | null;
  enabled?: boolean;
  onMessage: (event: RealtimeMessageEvent) => void;
  /**
   * Fired when the stream opens AGAIN after a drop: frames sent in between were lost, so the caller
   * refetches the conversation. Not fired for the first open, nor for opening another chat (that one loads
   * its own history), and at most once until a connection stays open `STABLE_OPEN_MS` (no resync storm).
   */
  onReopen?: () => void;
}

/**
 * Opens an SSE stream against the BE's `/api/web/realtime/:chatId`
 * endpoint and fires `onMessage` for each event.
 *
 * **Auth**: the standard browser `EventSource` API doesn't allow
 * custom headers, so the JWT rides on the URL as a `?token=` param.
 * This is fine because (a) the URL never leaves the visitor's
 * browser, (b) the token is short-lived (~10 min), and (c) the BE's
 * `VisitorJwtGuard` accepts it via query in addition to header
 * (small accommodation made for SSE specifically).
 *
 * **Reconnect**: handled by the browser automatically — when the
 * connection drops, `EventSource` retries with backoff. We don't
 * intervene.
 *
 * **Typing events** fire `kind: 'typing'` and don't carry text — the
 * caller renders a "X is typing…" indicator until the next non-typing
 * event for the same chat.
 */
export function useVisitorRealtime({
  chatId,
  token,
  visitorId = null,
  enabled = true,
  onMessage,
  onReopen,
}: UseVisitorRealtimeOpts): void {
  const instance = useWidgetInstance();
  // Keep the callback in a ref so changes don't reopen the SSE stream
  // — we'd lose events during the reconnect window otherwise.
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const onReopenRef = useRef(onReopen);
  useEffect(() => {
    onReopenRef.current = onReopen;
  }, [onReopen]);

  // The JWT only authorises the CONNECT: a silent renew (every few seconds for a short-lived preview
  // token) must not reopen the stream. A reconnect after a drop reads the latest token.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const hasToken = !!token;

  // biome-ignore lint/correctness/useExhaustiveDependencies: visitorId is a deliberate reconnect trigger (another visitor), not read in the body
  useEffect(() => {
    if (!enabled || !chatId || !hasToken) return;
    if (typeof window === "undefined") return;

    // `WebOriginGuard` requires the integration's publicKey on every
    // visitor request. `EventSource` can't set custom headers, so we
    // attach it as a query param — the guard accepts it there (it's
    // already a documented fallback for SSE-style transports).
    const publicKey = instance.publicKey;
    if (!publicKey) {
      // Without a publicKey the BE would reject with
      // `webMissingPublicKey`; bail before opening the stream rather
      // than burning a reconnect cycle.
      return;
    }
    let disposed = false;
    let attempts = 0;
    // Per stream (per chat): the first open of THIS chat is a fresh load, only a later one follows a gap.
    let openedBefore = false;
    let resyncArmed = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let stableTimer: ReturnType<typeof setTimeout> | undefined;
    let teardownSource: () => void = () => {};

    const connect = () => {
      const token = tokenRef.current;
      if (!token) return;
      const url =
        `${apiBase()}/api/web/realtime/${encodeURIComponent(chatId)}` +
        `?token=${encodeURIComponent(token)}` +
        `&publicKey=${encodeURIComponent(publicKey)}`;
      const source = new EventSource(url, { withCredentials: false });

      const handle = (event: MessageEvent) => {
        try {
          const parsed = JSON.parse(event.data) as RealtimeMessageEvent;
          onMessageRef.current(parsed);
        } catch {
          // Malformed payload — log + skip. The SSE stream itself
          // remains open; one bad frame doesn't kill the connection.
          // eslint-disable-next-line no-console
          console.warn("[wexio] unparseable SSE frame", event.data);
        }
      };
      // `messageUpdate` is an EDIT of an existing message: tag it so the consumer patches the
      // bubble in place instead of dropping it as a duplicate id.
      const handleUpdate = (event: MessageEvent) => {
        try {
          const parsed = JSON.parse(event.data) as RealtimeMessageEvent;
          onMessageRef.current({ ...parsed, update: true });
        } catch {
          // eslint-disable-next-line no-console
          console.warn("[wexio] unparseable SSE frame", event.data);
        }
      };
      const handleDelete = (event: MessageEvent) => {
        try {
          const parsed = JSON.parse(event.data) as RealtimeMessageEvent;
          onMessageRef.current({ ...parsed, kind: "text", deleted: true });
        } catch {
          // eslint-disable-next-line no-console
          console.warn("[wexio] unparseable SSE frame", event.data);
        }
      };
      const handleOpen = () => {
        if (openedBefore && resyncArmed) {
          resyncArmed = false;
          onReopenRef.current?.();
        }
        openedBefore = true;
        clearTimeout(stableTimer);
        stableTimer = setTimeout(() => {
          attempts = 0;
          resyncArmed = true;
        }, STABLE_OPEN_MS);
      };
      // A dropped stream: we reconnect (latest token, growing backoff) instead of the browser's fixed
      // ~3 s retry. A coded `error` FRAME is a MessageEvent, left to the preview watcher.
      const handleError = (event: Event) => {
        if (event instanceof MessageEvent) return;
        clearTimeout(stableTimer);
        teardownSource();
        if (disposed) return;
        retryTimer = setTimeout(
          connect,
          Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempts),
        );
        attempts += 1;
      };

      source.addEventListener("open", handleOpen);
      source.addEventListener("message", handle);
      source.addEventListener("messageUpdate", handleUpdate as EventListener);
      source.addEventListener("messageDelete", handleDelete as EventListener);
      // Registered before our error listener so it still sees a refused connect as CLOSED.
      const stopWatching = watchPreviewStream(source, instance, token);
      source.addEventListener("error", handleError);
      teardownSource = () => {
        stopWatching();
        source.removeEventListener("open", handleOpen);
        source.removeEventListener("message", handle);
        source.removeEventListener("messageUpdate", handleUpdate as EventListener);
        source.removeEventListener("messageDelete", handleDelete as EventListener);
        source.removeEventListener("error", handleError);
        source.close();
      };
    };

    connect();
    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      clearTimeout(stableTimer);
      teardownSource();
    };
  }, [chatId, hasToken, visitorId, enabled, instance]);
}
